/**
 * Quality profiles.
 *
 * One table mapping a device tier to every render setting the scenes consult.
 * Keeping it here rather than scattering `isMobile ? 8 : 32` through the scene
 * files means the whole visual/performance trade-off can be read, reviewed and
 * tuned in one place.
 *
 * The numbers are deliberately conservative on the low tier: the brief targets a
 * mid-range Android, where fill rate - not polygon count - is what drops frames.
 * So the low tier cuts pixel ratio, shadows and post-processing first, and
 * geometry detail second.
 */

export type QualityTier = 'low' | 'medium' | 'high';

export interface QualityProfile {
  tier: QualityTier;
  /** Upper bound on device pixel ratio. */
  maxPixelRatio: number;
  /** Real-time shadows at all. Baked ambient occlusion carries the low tier. */
  shadows: boolean;
  shadowMapSize: number;
  /** Restrained bloom only; never a full post stack. */
  bloom: boolean;
  bloomIntensity: number;
  /** Atmospheric mist particle count. */
  particleCount: number;
  /** Radial segments for rings, dials and tubes. */
  ringSegments: number;
  /** Tubular segments for rope and light-channel geometry. */
  tubeSegments: number;
  /** Sphere/curve subdivision for the gloves and heavy bag. */
  surfaceSegments: number;
  /** Environment map resolution for the studio reflections. */
  envMapSize: number;
  /** Anisotropic filtering on the few detail textures. */
  anisotropy: number;
  /** Whether the heavy bag and ropes run their physics approximation. */
  physics: boolean;
  /** Frame budget in ms before the performance monitor steps quality down. */
  frameBudgetMs: number;
  antialias: boolean;
}

export const QUALITY_PROFILES: Record<QualityTier, QualityProfile> = {
  high: {
    tier: 'high',
    maxPixelRatio: 2,
    shadows: true,
    shadowMapSize: 2048,
    bloom: true,
    bloomIntensity: 0.34,
    particleCount: 420,
    ringSegments: 128,
    tubeSegments: 96,
    surfaceSegments: 64,
    envMapSize: 256,
    anisotropy: 4,
    physics: true,
    frameBudgetMs: 20,
    antialias: true,
  },
  medium: {
    tier: 'medium',
    maxPixelRatio: 1.75,
    shadows: true,
    shadowMapSize: 1024,
    bloom: true,
    bloomIntensity: 0.24,
    particleCount: 200,
    ringSegments: 72,
    tubeSegments: 48,
    surfaceSegments: 40,
    envMapSize: 128,
    anisotropy: 2,
    physics: true,
    frameBudgetMs: 24,
    antialias: true,
  },
  low: {
    tier: 'low',
    maxPixelRatio: 1.25,
    shadows: false,
    shadowMapSize: 512,
    bloom: false,
    bloomIntensity: 0,
    particleCount: 0,
    ringSegments: 40,
    tubeSegments: 24,
    surfaceSegments: 24,
    envMapSize: 64,
    anisotropy: 1,
    physics: false,
    frameBudgetMs: 30,
    antialias: false,
  },
};

export function profileFor(tier: QualityTier): QualityProfile {
  return QUALITY_PROFILES[tier];
}

/**
 * Reduced motion does not mean "no scene" - it means no camera travel, no
 * continuous rotation and no parallax. The scene still renders, composed at a
 * resting pose, and transitions become opacity and scale. This returns the
 * multipliers the scenes apply to every animated quantity.
 */
export interface MotionProfile {
  /** Scales all rotation speeds. Zero under reduced motion. */
  rotation: number;
  /** Scales camera travel distance. Zero under reduced motion. */
  cameraTravel: number;
  /** Scales cursor parallax. Zero under reduced motion. */
  parallax: number;
  /** Scales layer separation distance. Reduced, not eliminated. */
  separation: number;
  /** Whether scroll-linked opacity transitions are used instead of movement. */
  preferOpacity: boolean;
}

export const FULL_MOTION: MotionProfile = {
  rotation: 1,
  cameraTravel: 1,
  parallax: 1,
  separation: 1,
  preferOpacity: false,
};

export const REDUCED_MOTION: MotionProfile = {
  rotation: 0,
  cameraTravel: 0,
  parallax: 0,
  // Layers still resolve to their separated pose, they simply do not animate
  // there - otherwise a reduced-motion visitor would see an unreadable
  // mid-timeline composition.
  separation: 1,
  preferOpacity: true,
};

export function motionProfileFor(prefersReducedMotion: boolean): MotionProfile {
  return prefersReducedMotion ? REDUCED_MOTION : FULL_MOTION;
}
